/**
 * HustleProfitMapsSalesPage.jsx
 * ─────────────────────────────
 * Sales page for HustleProfitMaps — the agency-grade local-rank grid
 * built into HustleSuite. Rendered on the bare domain
 * hustleprofitmaps.au (+ www / demo) via RootRouter; reachable in-app
 * at /admin/exposure-grid for live scans.
 *
 * Fully theme-aware: uses the suite's Tailwind dark-mode system
 * (darkMode:"class" + .dark on <html>), mirroring the compliant module
 * sales pages (e.g. HustleAnswerSalesPage). Global black top-bar +
 * PlatformHeader + SuiteFooter provide the shared chrome.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Printer, MapPin, Map as MapIcon, Radar,
  Sparkles, FileText, Search, TrendingUp, Layers, Building2, BarChart3,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";

const IMG_HERO   = "https://images.unsplash.com/photo-1754299343336-4240f3109a06?w=1400&q=80&auto=format&fit=crop";
const IMG_GRID   = "https://images.pexels.com/photos/8828593/pexels-photo-8828593.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_SUBURB = "https://images.pexels.com/photos/33440163/pexels-photo-33440163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_DASH   = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80&auto=format&fit=crop";
const IMG_OWNER  = "https://images.pexels.com/photos/36729739/pexels-photo-36729739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_SEARCH = "https://images.unsplash.com/photo-1516210938721-0703012a297a?w=1200&q=80&auto=format&fit=crop";

const OPEN = "/login?next=/admin/exposure-grid";

const CAPS = [
  { icon: Radar, t: "Rank grid heat-map",
    b: "Drop a lattice of up to 15×15 points over any business and see its exact rank at every point — a green cluster where you own the suburb, red where you're invisible. The Lensly-style map local agencies charge hundreds for." },
  { icon: Layers, t: "Organic + Google Maps layers",
    b: "Toggle between your Organic rank (what Google + AI answer engines crawl), the Google Maps map-pack rank (true local-pack position via DataForSEO), or a Combined view — on one grid." },
  { icon: Sparkles, t: "AI action plan",
    b: "Every scan writes a plain-English 'how to climb' plan — the exact suburbs to target, the location pages to publish, the GBP signals to fix — generated from your real grid, not a template." },
  { icon: FileText, t: "White-label PDF reports",
    b: "Turn any scan into a branded, shareable PDF or link. Hand it to a client as the agency deliverable that justifies the retainer — your logo, your colours, your numbers." },
  { icon: Search, t: "Business Finder prospector",
    b: "Powered by our 24,000-business Australian directory. Find under-ranking businesses in any niche + suburb, scan their grid in one click, and walk in with the proof already printed." },
  { icon: TrendingUp, t: "Exposure over time",
    b: "Save a snapshot after every change and watch the before/after. The exposure-score trend line is the clearest proof that the work you did actually moved the needle." },
];

const CHAPTERS = [
  { kicker: "Chapter 1 · rank is a map, not a number",
    title: "Your ranking changes with every street corner.",
    body: "Ask most SEO tools 'where do I rank for coffee near me?' and you get a single number. But local rank is not a number — it's a map. Stand outside the café and you might be #1; drive two suburbs over and you've vanished, because Google weighs the searcher's location heavily for local queries. HustleProfitMaps makes that visible: it drops a grid of search points across the area around a business and checks the real ranking at each one. The result is a heat-map — a green cluster around 'home' where the business dominates, fading to amber then red where it's invisible. Every red point is a customer searching right now and never seeing you.",
    img: IMG_GRID },
  { kicker: "Chapter 2 · organic + the Google Maps map-pack",
    title: "Two ranking worlds, one lattice.",
    body: "There are two local-rank worlds that matter, and HustleProfitMaps maps both. The Organic layer reflects your position in classic results and — increasingly — what AI answer engines surface. The Google Maps layer reflects the map-pack: the three-business box that sits above everything for 'near me' searches, pulled live from DataForSEO's Google-Maps endpoint so every point is a true local-pack position. Flip between Organic, Maps and Combined on the same grid, and you instantly see the gap — the business that ranks organically but is nowhere in the map-pack, or vice versa. That gap is almost always the fastest, cheapest win available.",
    img: IMG_SUBURB },
  { kicker: "Chapter 3 · the AI action plan",
    title: "A scan is useless without the next step.",
    body: "A heat-map is a diagnosis. HustleProfitMaps also writes the prescription. The moment a scan finishes, our AI reads the actual grid — which suburbs are red, which keywords lag, where competitors are eating your lunch — and writes a plain-English plan ranked by impact. Publish a location page for Ashgrove and Red Hill. Fix the primary category on the Google Business Profile. Chase five reviews from Paddington customers to lift the map-pack. No fluff, no generic checklist — a specific plan tied to this business's real visibility, written in seconds. It turns a 'nice chart' into a to-do list a staff member can start on Monday.",
    img: IMG_DASH },
  { kicker: "Chapter 4 · the report that wins the retainer",
    title: "White-label proof clients actually understand.",
    body: "The hardest part of selling local SEO is proving it's worth the money. A red-to-green grid does the selling for you. HustleProfitMaps turns any scan into a clean, branded, shareable report — a PDF or live link with your logo, your colours and the client's own numbers. Send the 'before' grid when you pitch; the 'after' grid when you've done the work. The exposure-score trend line sitting next to the map is the clearest ROI story a non-technical owner will ever see — and it's the reason retainers get renewed.",
    img: IMG_OWNER },
  { kicker: "Chapter 5 · prospect with Business Finder",
    title: "Walk in with the proof already printed.",
    body: "HustleProfitMaps sits on top of our 24,000-business Australian directory, so you never scan blind. Pick a niche and a suburb, and Business Finder surfaces the businesses under-ranking right now. One click builds their grid; one more prints the white-label report. Instead of cold-pitching 'we do SEO', you walk in holding a map of exactly where that café, plumber or clinic is invisible across their own suburb — and the plan to fix it.",
    img: IMG_SEARCH },
];

const HOW = [
  { n: "01", icon: Search, t: "Search a business",
    b: "Type any business name. HustleProfitMaps pulls its location and niche from the directory and sets up the grid — no setup, no spreadsheets." },
  { n: "02", icon: Radar, t: "Run the grid",
    b: "Pick your radius and grid size, choose Organic, Maps or Combined, and scan. The heat-map renders point-by-point with live metrics in seconds." },
  { n: "03", icon: FileText, t: "Export the plan",
    b: "Save a snapshot, generate the AI action plan, and export a white-label PDF or link to hand straight to the client." },
];

const TIERS = [
  { key: "starter", name: "Starter", price: "Included", period: "in your Suite",
    cta: { label: "Open your grid", href: OPEN },
    blurb: "Every HustleSuite business gets the Organic rank grid built in. Scan your own listings, see your suburb reach, and track exposure over time — free.",
    features: ["Organic rank grid (up to 13×13)", "Suburb-reach + exposure-score metrics",
      "Snapshot history (before / after)", "Works on your HustleDirectory listings", "Lives inside your admin console"] },
  { key: "agency", name: "Agency", price: "$49", period: "/mo", badge: "Most popular", highlight: true,
    cta: { label: "Start the Agency plan", href: "/claim-business?product=profitmaps&tier=agency" },
    blurb: "For operators and agencies managing visibility for clients. Adds the live Google-Maps map-pack layer, AI action plans and white-label reporting.",
    features: ["Everything in Starter, plus:", "Google Maps map-pack layer (live DataForSEO)",
      "Organic / Maps / Combined toggle", "AI action plan on every scan",
      "White-label PDF + shareable report links", "Business Finder prospector (24k directory)",
      "Cost-preview before every paid scan"] },
  { key: "scale", name: "Scale", price: "Contact us", period: "",
    cta: { label: "Book a call", href: "/claim-business?product=profitmaps&tier=scale" },
    blurb: "For multi-location groups and agencies running scans at volume. Bulk scheduling, API access and a dedicated success manager.",
    features: ["Everything in Agency, plus:", "Scheduled recurring scans + alerts",
      "Bulk / multi-location dashboards", "API access for your own reports",
      "Volume DataForSEO pricing", "Dedicated success manager"] },
];

const TESTIMONIALS = [
  { name: "Dan · Brisbane SEO studio", quote: "The red-to-green grid closes deals for me. I used to pay for an overseas enterprise tool just for this one chart. Now it's in the same login as everything else, and the white-label PDF is sharper." },
  { name: "Priya · multi-site café group", quote: "We found three suburbs where we were completely invisible in the map-pack. Published location pages, re-scanned a month later, and the exposure score nearly doubled." },
  { name: "Marcus · regional marketing agency", quote: "Business Finder plus the grid is a prospecting machine. I walk into a meeting with a printed map of exactly where they're losing customers. It barely feels like selling." },
];

const FAQS = [
  { q: "What exactly is HustleProfitMaps?",
    a: "A local-rank grid tool. It drops a lattice of search points around a business and checks its ranking at every point, rendering a heat-map of where it's visible and where it isn't — across organic results and the Google Maps map-pack. Think Lensly or a ProfitMaps-style geo-grid, built into HustleSuite." },
  { q: "How do the Google Maps rankings work — and do they cost money?",
    a: "The Maps layer pulls true map-pack positions from DataForSEO's Google-Maps endpoint. Those are paid API calls — roughly one credit per grid point (a 7×7 scan is ~$0.03). HustleProfitMaps always shows a cost preview before a paid scan and lets you run a small test first, so nothing is spent without you seeing the number." },
  { q: "Is the Organic grid free?",
    a: "Yes. The Organic rank grid, suburb-reach metrics and exposure-score history are included for every HustleSuite business at no cost — it runs on data we already hold, with no paid API calls." },
  { q: "Can I put my own brand on the reports?",
    a: "On the Agency plan, yes. Turn any scan into a white-label PDF or shareable link with your logo and colours — the deliverable you hand a client to justify the retainer." },
  { q: "Where's the data from?",
    a: "Business, niche and location data comes from our 24,000-business Australian directory. Organic positions are computed from that index; Google-Maps positions come live from DataForSEO. Every scan is on real businesses, not samples." },
  { q: "How is this different from BrightLocal or Local Falcon?",
    a: "Same core idea — a geo-grid of local rankings — but HustleProfitMaps lives inside HustleSuite alongside your directory, reviews and CRM, writes an AI action plan from each scan, and ships Business Finder so you can prospect from the directory. One login, Australian data, agency-ready reports." },
];

/* ── reusable bits ── */
const kicker = "text-xs uppercase tracking-[0.25em] font-bold text-teal-600 dark:text-teal-400";
const h2cls = "text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight text-stone-900 dark:text-white";
const btnPrimary = "inline-flex items-center gap-2 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-semibold px-7 h-12 text-base transition-colors";
const btnGhost = "inline-flex items-center gap-2 rounded-full border border-stone-300 dark:border-stone-700 bg-transparent text-stone-800 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 font-semibold px-7 h-12 text-base transition-colors";

function StatPill({ num, label }) {
  return (
    <div className="inline-flex items-baseline gap-2 rounded-full border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 px-4 py-2">
      <strong className="text-lg font-extrabold text-stone-900 dark:text-white">{num}</strong>
      <span className="text-sm text-stone-600 dark:text-stone-400">{label}</span>
    </div>
  );
}

function TierCard({ tier }) {
  return (
    <div data-testid={`profitmaps-tier-${tier.key}`}
         className={`relative flex flex-col gap-4 rounded-2xl p-7 bg-white dark:bg-stone-900 ${
           tier.highlight
             ? "border-2 border-teal-500 shadow-xl shadow-teal-500/10"
             : "border border-stone-200 dark:border-stone-800 shadow-sm"}`}>
      {tier.badge && (
        <span className="absolute -top-3 right-5 rounded-full bg-gradient-to-br from-teal-600 to-emerald-700 text-white text-[11px] font-bold px-3 py-1 tracking-wide">
          {tier.badge}
        </span>
      )}
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-teal-600 dark:text-teal-400">{tier.name}</div>
        <div className="mt-1.5 text-4xl font-extrabold text-stone-900 dark:text-white">
          {tier.price}<span className="text-base font-medium text-stone-500 dark:text-stone-400 ml-1.5">{tier.period}</span>
        </div>
      </div>
      <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">{tier.blurb}</p>
      <ul className="grid gap-2">
        {tier.features.map((f, i) => (
          <li key={i} className="flex gap-2 text-sm text-stone-800 dark:text-stone-200 leading-snug">
            <Check size={16} className="shrink-0 mt-0.5 text-teal-600 dark:text-teal-400" /><span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`profitmaps-tier-${tier.key}-cta`}
            className={`${btnPrimary} self-start mt-1 h-11 px-6 text-sm`}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

const PrintStyles = () => (
  <style>{`@media print{[data-testid="profitmaps-print-btn"],[data-hp-founder-chat]{display:none!important}body{background:#fff!important}}`}</style>
);

export default function HustleProfitMapsSalesPage() {
  useEffect(() => {
    document.title = "HustleProfitMaps — See exactly where you rank, street by street.";
  }, []);

  return (
    <div data-testid="hustleprofitmaps-sales-page"
         className="min-h-screen bg-white text-stone-900 dark:bg-[#0a1512] dark:text-stone-100 transition-colors">
      <PrintStyles />
      <PlatformHeader salesTopGap
        brand={{ name: "HustleProfitMaps", to: "/" }}
        nav={[
          { to: "#how", label: "How it works" },
          { to: "#pricing", label: "Pricing" },
          { to: OPEN, label: "Open the grid", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="profitmaps-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(13,148,136,0.16),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className={`${kicker} mb-5 inline-flex items-center gap-2`}>
              <MapPin size={16} className="text-teal-600 dark:text-teal-400" /> HustleProfitMaps · local-rank intelligence
            </p>
            <h1 data-testid="profitmaps-hero-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.06] tracking-tight text-stone-900 dark:text-white">
              See exactly where you rank —
              <span className="text-teal-600 dark:text-teal-400"> street by street.</span>
            </h1>
            <p className="text-base md:text-lg text-stone-600 dark:text-stone-300 mt-6 max-w-xl leading-relaxed">
              HustleProfitMaps drops a live grid over any business and maps its ranking at every
              point across the suburb — organic <em>and</em> the Google Maps map-pack. Spot the red
              zones where you're invisible, get an AI plan to turn them green, and prove the exposure
              you win with white-label reports. <strong className="text-stone-800 dark:text-stone-100">The agency-grade local-rank tool built into HustleSuite.</strong>
            </p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              <StatPill num="15×15" label="grid heat-map" />
              <StatPill num="Maps + Organic" label="rank layers" />
              <StatPill num="AI" label="action plan" />
              <StatPill num="White-label" label="reports" />
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={OPEN} data-testid="profitmaps-cta-open" className={btnPrimary}>
                Open your grid <ArrowRight size={16} />
              </Link>
              <Link to="/claim-business?product=profitmaps&tier=agency" data-testid="profitmaps-cta-agency" className={btnGhost}>
                <MapIcon size={16} /> Go Agency
              </Link>
              <button data-testid="profitmaps-print-btn" onClick={() => window.print()} className={btnGhost}>
                <Printer size={16} /> Print proposal
              </button>
            </div>
          </div>
          <div className="relative">
            <img src={IMG_HERO} alt="Local map with ranked location points"
                 className="rounded-2xl shadow-2xl shadow-teal-950/30 border border-stone-200 dark:border-stone-800 w-full aspect-[4/3] object-cover" />
            <div className="absolute -bottom-4 -right-4 bg-teal-600 text-white px-4 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-1">
              <Sparkles size={14} /> Live in under a minute
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60" data-testid="profitmaps-caps">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className={`${kicker} mb-4`}>What's in the box</p>
          <h2 className={`${h2cls} mb-10 max-w-3xl`}>Six tools in one local-rank engine.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPS.map((c, i) => (
              <div key={i} data-testid={`profitmaps-cap-${i}`}
                   className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-6 hover:border-teal-500/60 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 flex items-center justify-center mb-4">
                  <c.icon size={20} strokeWidth={1.75} />
                </div>
                <h3 className="font-bold text-stone-900 dark:text-stone-100">{c.t}</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 mt-2 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid="profitmaps-chapters">
        <div className="space-y-16">
          {CHAPTERS.map((ch, i) => (
            <article key={i} data-testid={`profitmaps-chapter-${i}`}
                     className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <img src={ch.img} alt={ch.title}
                   className={`w-full aspect-[16/10] object-cover rounded-2xl border border-stone-200 dark:border-stone-800 shadow-lg ${i % 2 ? "lg:order-2" : ""}`} />
              <div className={i % 2 ? "lg:order-1" : ""}>
                <p className={`${kicker} mb-3`}>{ch.kicker}</p>
                <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-stone-900 dark:text-white mb-4 leading-tight">{ch.title}</h3>
                <p className="text-base text-stone-600 dark:text-stone-300 leading-relaxed">{ch.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60" data-testid="profitmaps-how">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className={`${kicker} mb-4`}>Go live in three steps</p>
          <h2 className={`${h2cls} mb-10`}>Search → Scan → Export.</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {HOW.map((s) => (
              <div key={s.n} data-testid={`profitmaps-step-${s.n}`}
                   className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-6 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-extrabold text-teal-600 dark:text-teal-400 leading-none">{s.n}</span>
                  <s.icon size={20} className="text-teal-700 dark:text-teal-300" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 dark:text-stone-100">{s.t}</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid="profitmaps-tiers">
        <p className={`${kicker} mb-4`}>Pricing</p>
        <h2 className={`${h2cls} mb-3`}>Free organic grids. Agency power when you're ready.</h2>
        <p className="text-base text-stone-600 dark:text-stone-400 max-w-3xl leading-relaxed mb-10">
          Every HustleSuite business gets the organic rank grid for free. The Agency plan adds the
          live Google-Maps map-pack layer, AI action plans, Business Finder and white-label reports —
          with a cost preview before every paid scan so you're always in control of spend.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60" data-testid="profitmaps-testimonials">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className={`${kicker} mb-4`}>Operators + agencies on HustleProfitMaps</p>
          <h2 className={`${h2cls} mb-10`}>The people running it, in their words.</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {TESTIMONIALS.map((tm, i) => (
              <blockquote key={i} data-testid={`profitmaps-testimonial-${i}`}
                          className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-6">
                <BarChart3 size={18} className="text-teal-600 dark:text-teal-400 mb-3" />
                <p className="text-base italic text-stone-800 dark:text-stone-200 leading-relaxed mb-3">"{tm.quote}"</p>
                <footer className="text-xs uppercase tracking-[0.14em] font-bold text-teal-600 dark:text-teal-400">— {tm.name}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid="profitmaps-faq">
        <p className={`${kicker} mb-4`}>Honest FAQ</p>
        <h2 className={`${h2cls} mb-8`}>Questions people actually ask.</h2>
        <div className="grid gap-3">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`profitmaps-faq-${i}`}
                     className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
              <summary className="text-lg font-semibold text-stone-900 dark:text-stone-100 cursor-pointer">{f.q}</summary>
              <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20" data-testid="profitmaps-final-cta">
        <div className="rounded-3xl bg-gradient-to-br from-teal-700 via-teal-800 to-emerald-900 text-white p-9 sm:p-12 flex flex-wrap gap-6 items-center">
          <div className="flex-1 min-w-[260px]">
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">Map your visibility. Win the suburb.</h2>
            <p className="text-base text-white/85 leading-relaxed max-w-xl">
              Run your first grid in under a minute. See where you're invisible, get the AI plan,
              and hand clients a white-label report that proves the exposure you win.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={OPEN} data-testid="profitmaps-final-open"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-teal-800 font-bold px-7 h-12 text-base hover:bg-stone-100 transition-colors">
              Open your grid <ArrowRight size={16} />
            </Link>
            <Link to="/claim-business?product=profitmaps&tier=agency" data-testid="profitmaps-final-agency"
                  className="inline-flex items-center gap-2 rounded-full border border-white/70 text-white font-semibold px-7 h-12 text-base hover:bg-white/10 transition-colors">
              <Building2 size={16} /> Talk about Agency
            </Link>
          </div>
        </div>
      </section>

      <FounderChatPill testid="profitmaps-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
